import { generatePath } from 'react-router-dom';

import { SECURITY_JOURNEY_ROUTE } from 'ae-constants/routes.constants';

export type FocusIDParam = {
  focusID: string;
};

export type SpidrIDParam = {
  spidrID: string;
};

export type TicketIDParam = {
  ticketID: string;
};

export const ROUTE_CONSTANTS = {
  ALL: 'all',
  BLUEPRINT_ID_PATH_PARAM: ':blueprintID',
  FAVORITES: 'favorites',
  FOCUS_ID: ':focusID',
  EXCLUDED: 'excluded',
  INCLUDED: 'included',
  SECURITY_JOURNEY: SECURITY_JOURNEY_ROUTE,
  SECURITY_FOCUSES: 'security-focuses',
  SPIDRS: 'spidrs',
  SPIDR_LIBRARY: 'spidr-library',
  SPIDR_ID: ':spidrID',
  TICKETS: 'tickets',
  TICKET_ID: ':ticketID',
  BLUEPRINT_ID_SEARCH_PARAM: 'blueprintID',
  RESOURCE_CENTER: 'resource-center',
  CONTACT_CONCIERGE: 'contact-concierge',
};

/** prettier-ignore */
export const routePaths = {
  index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}`,
  securityFocuses: {
    index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SECURITY_FOCUSES}`,
    focusID: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SECURITY_FOCUSES}/${ROUTE_CONSTANTS.FOCUS_ID}`,
      spidrs: {
        spidrID: {
          index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SECURITY_FOCUSES}/${ROUTE_CONSTANTS.FOCUS_ID}/${ROUTE_CONSTANTS.SPIDRS}/${ROUTE_CONSTANTS.SPIDR_ID}`,
        },
      },
    },
  },
  spidrs: {
    spidrID: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDRS}/${ROUTE_CONSTANTS.SPIDR_ID}`,
    },
  },
  spidrLibrary: {
    index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDR_LIBRARY}`,
    all: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDR_LIBRARY}/${ROUTE_CONSTANTS.ALL}`,
    },
    included: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDR_LIBRARY}/${ROUTE_CONSTANTS.INCLUDED}`,
    },
    excluded: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDR_LIBRARY}/${ROUTE_CONSTANTS.EXCLUDED}`,
    },
    favorites: {
      index: `${ROUTE_CONSTANTS.SECURITY_JOURNEY}/${ROUTE_CONSTANTS.SPIDR_LIBRARY}/${ROUTE_CONSTANTS.FAVORITES}`,
    },
  },
  tickets: {
    ticketID: {
      index: `/${ROUTE_CONSTANTS.TICKETS}/${ROUTE_CONSTANTS.TICKET_ID}`,
    },
  },
  resourceCenter: {
    contactConcierge: {
      index: `/${ROUTE_CONSTANTS.RESOURCE_CENTER}/${ROUTE_CONSTANTS.CONTACT_CONCIERGE}`,
    },
  },
};

/**
 * A collection of route functions that are used to support navigation via links or otherwise
 *
 * @example
 * ```tsx
 * <Link to={routes.securityFocuses.focusID.index({ focusID: "my focusID" })}>
 *   Navigate to Focus
 * </Link>
 * ```
 */
export const routes = {
  index: () => generatePath(routePaths.index),
  securityFocuses: {
    index: () => generatePath(routePaths.securityFocuses.index),
    focusID: {
      index: (params: FocusIDParam) =>
        generatePath(routePaths.securityFocuses.focusID.index, params),
      spidrs: {
        spidrID: {
          index: (params: FocusIDParam & SpidrIDParam) =>
            generatePath(
              routePaths.securityFocuses.focusID.spidrs.spidrID.index,
              params,
            ),
        },
      },
    },
  },
  spidrs: {
    spidrID: {
      index: (params: SpidrIDParam) =>
        generatePath(routePaths.spidrs.spidrID.index, params),
    },
  },
  spidrLibrary: {
    index: () => generatePath(routePaths.spidrLibrary.index),
    all: {
      index: () => generatePath(routePaths.spidrLibrary.all.index),
    },
    included: {
      index: () => generatePath(routePaths.spidrLibrary.included.index),
    },
    excluded: {
      index: () => generatePath(routePaths.spidrLibrary.excluded.index),
    },
    favorites: {
      index: () => generatePath(routePaths.spidrLibrary.favorites.index),
    },
  },
  tickets: {
    ticketID: {
      index: (params: TicketIDParam) =>
        generatePath(routePaths.tickets.ticketID.index, params),
    },
  },
  resourceCenter: {
    contactConcierge: {
      index: () =>
        generatePath(routePaths.resourceCenter.contactConcierge.index),
    },
  },
};
